import logger from '../../utils/logger';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { capitalCase } from 'change-case';
import isEmpty from 'is-empty';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { ENERGY } from '../../common/utils/constants';
import {
  calculateOrderAmount,
  calculateResourceTron,
  toSun,
  toTron,
} from '../../common/utils';
import { fillOrder } from '../../common/api';

const FillOrder = ({ isOpen, onClose, orderData, setOrderData }) => {
  const { accountAddress, accountBalance, accountDetails, noWalletDetect } =
    useSelector((state) => state.account);
  const toast = useToast();
  const [isOrderValid, setOrderValid] = useState(false);
  const [sharePercentage, setSharePercentage] = useState(0);
  const [isWalletBalanceSufficient, setIsWalletBalanceSufficient] =
    useState(false);
  const [minimumUserIncome, setMinimumUserIncome] = useState(4);
  const [orderLoader, setOrderLoader] = useState(false);

  const checkWalletBalanceSufficient = (resource_type, resource_amount) => {
    if (isEmpty(accountDetails)) return;
    let resourceAvailable =
      resource_type === ENERGY
        ? accountDetails.maxDelegatableEnergy
        : accountDetails.maxDelegatableBandwidth;
    let requiredResource = resource_amount - resourceAvailable;
    accountBalance >
    calculateResourceTron(resource_type, requiredResource, accountDetails)
      ? setIsWalletBalanceSufficient(false)
      : setIsWalletBalanceSufficient(true);
  };

  const calculateIncome = (resourceAmount, i) => {
    if (isEmpty(resourceAmount)) return 0;
    return toTron(
      (sharePercentage / 100) *
        calculateOrderAmount(
          parseInt(resourceAmount),
          i.price_per_day,
          i.freeze_duration
        )
    );
  };

  const checkOrderValid = () => {
    parseInt(orderData?.resourceAmount) <= orderData?.resource_amount ||
    minimumUserIncome <= calculateIncome(orderData?.resourceAmount, orderData)
      ? setOrderValid(true)
      : setOrderValid(false);
  };

  const calculateMinOrderFromIncome = (freezeDuration, value) => {
    return Math.ceil(
      toSun(
        minimumUserIncome / ((sharePercentage / 100) * value * freezeDuration)
      )
    );
  };

  const updateFillOrderInput = (value) => {
    if (isEmpty(accountDetails)) return;
    let freezeAmount = calculateResourceTron(
      orderData.resource_type,
      value,
      accountDetails
    );
    setOrderData({
      ...orderData,
      freezeAmount,
      resourceAmount: value,
    });
  };

  const finalResourcesRequired = (resourceAmount, resource_type) => {
    if (isEmpty(accountAddress) || isEmpty(accountDetails)) return '-';
    const { maxDelegatableEnergy } = accountDetails;
    const remainingResourceAmount =
      parseInt(resourceAmount) - maxDelegatableEnergy;
    const resourceTron = calculateResourceTron(
      resource_type,
      remainingResourceAmount,
      accountDetails
    );
    let formattedEnergy = !isEmpty(maxDelegatableEnergy)
      ? maxDelegatableEnergy.toFixed(2)
      : 0;
    if (isEmpty(resourceAmount) || resourceAmount > orderData.resource_amount)
      return (
        calculateResourceTron(
          resource_type,
          orderData.resource_amount,
          accountDetails
        ) + ' TRX'
      );
    if (remainingResourceAmount < 0)
      return `${resourceAmount} ${resource_type?.toUpperCase()}`;
    if (maxDelegatableEnergy <= 0) {
      return `${resourceTron} TRX`;
    } else {
      return `${resourceTron} TRX + ${formattedEnergy} ${resource_type?.toUpperCase()}`;
    }
  };

  const fillOrderHandler = async (order) => {
    if (noWalletDetect) {
      return toast({
        title: `Wallet not connected`,
        position: 'top-right',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    if (isEmpty(accountDetails)) {
      return toast({
        title: `Account details not loaded`,
        position: 'top-right',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    setOrderLoader(true);
    const res = await fillOrder(order, accountAddress, accountDetails);
    if (res?.data?.status) {
      toast({
        title: res?.data?.message ?? `Transaction completed successfully`,
        position: 'top-right',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    } else {
      toast({
        title: `Something went wrong`,
        position: 'top-right',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    setOrderLoader(false);
  };

  if (isEmpty(orderData)) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Fill Order</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel mt={4}>
              <span>{capitalCase(orderData?.resource_type ?? ' ')}</span> :{' '}
              {orderData?.resource_amount}
            </FormLabel>
            <InputGroup>
              <Input
                type={'number'}
                onChange={(e) => {
                  setOrderData((prev) => ({
                    ...prev,
                    resourceAmount: e.target.value,
                  }));
                  checkWalletBalanceSufficient(
                    orderData.resource_type,
                    e.target.value
                  );
                  checkOrderValid();
                }}
                placeholder={capitalCase(orderData?.resource_type ?? ' ')}
                // defaultValue={orderData?.resource_amount}
                value={orderData?.resourceAmount}
                max={orderData?.resource_amount}
                isInvalid={
                  minimumUserIncome >=
                  calculateIncome(orderData?.resourceAmount, orderData)
                }
              />
              <InputRightAddon
                children='Max'
                onClick={() => {
                  updateFillOrderInput(orderData?.resource_amount);
                  checkWalletBalanceSufficient(
                    orderData.resource_type,
                    orderData.resource_amount
                  );
                }}
                style={{ cursor: 'pointer' }}
              />
            </InputGroup>
            {isWalletBalanceSufficient && (
              <Text fontSize='xs' color='crimson' mt='1'>
                Insufficient Wallet Balance
              </Text>
            )}
            {minimumUserIncome >=
              calculateIncome(orderData?.resourceAmount, orderData) && (
              <Text fontSize='xs' color='crimson' mt='1'>
                Minimum Amount to freeze{' '}
                {calculateMinOrderFromIncome(
                  orderData.freeze_duration,
                  orderData.price_per_day
                ) +
                  ' ' +
                  orderData.resource_type}
              </Text>
            )}
            <FormControl>
              <FormLabel mt={5}>Receiving Income Address</FormLabel>
              <Input
                type='text'
                placeholder={'Wallet Address'}
                defaultValue={accountAddress}
                disabled={true}
              />
            </FormControl>
            <Box
              p='1'
              mt={5}
              boxShadow='md'
              rounded={'lg'}
              border='1px solid #ccc'
              fontSize={'16px'}
            >
              <Table variant='unstyled' size='sm'>
                <Tbody>
                  <Tr style={{ verticalAlign: 'text-bottom' }}>
                    <Td width={'165px'}>Available</Td>
                    <Td>: {accountBalance} TRX</Td>
                  </Tr>
                  <Tr style={{ verticalAlign: 'text-bottom' }}>
                    <Td width={'165px'}>Delegation Amount</Td>
                    <Td>
                      :{' '}
                      {finalResourcesRequired(
                        orderData?.resourceAmount,
                        orderData?.resource_type
                      )}
                    </Td>
                  </Tr>
                  <Tr style={{ verticalAlign: 'text-bottom' }}>
                    <Td width={'165px'}>Delegation Duration</Td>
                    <Td>: {orderData?.freeze_duration} Days</Td>
                  </Tr>
                  <Tr style={{ verticalAlign: 'text-bottom' }}>
                    <Td width={'165px'}>Income</Td>
                    <Td>
                      :{' '}
                      <span className='green'>
                        <b>
                          {calculateIncome(
                            orderData?.resourceAmount,
                            orderData
                          )}{' '}
                          TRX
                        </b>
                      </span>{' '}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button
            className='primary'
            mr={5}
            onClick={() => fillOrderHandler(orderData)}
            isDisabled={!isOrderValid}
            isLoading={orderLoader}
          >
            Fill Order
          </Button>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FillOrder;
